import { Component, Input, Output, EventEmitter, OnInit, OnDestroy, Inject, Optional, ViewChild, ElementRef } from '@angular/core';
import * as docx from 'docx-preview';
import * as XLSX from 'xlsx';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';

export interface FilePreviewData {
  url: string;
  name: string;
  title?: string;
  description?: string;
  type?: string;
  size?: number;
  base64?: string;
}

export type PreviewMode = 'modal' | 'inline' | 'thumbnail';
export type FileType =
  | 'image'
  | 'pdf'
  | 'docx'
  | 'xlsx'
  | 'video'
  | 'audio'
  | 'unknown';

@Component({
  selector: 'app-file-preview',
  templateUrl: './file-preview.component.html',
  styleUrls: ['./file-preview.component.scss']
})
export class FilePreviewComponent implements OnInit, OnDestroy {
  @Input() file: FilePreviewData | null = null;
  @Input() mode: PreviewMode = 'modal';
  @Input() maxWidth = '100%';
  @Input() maxHeight = '400px';
  @Input() showActions = true;
  @Input() showDownload = true;
  @Input() showFullscreen = true;
  @Input() autoOpen = false;

  @Output() closed = new EventEmitter<void>();
  @Output() downloaded = new EventEmitter<FilePreviewData>();
  @ViewChild('docxContainer', { static: false }) docxContainer!: ElementRef;
  @ViewChild('excelContainer', { static: false }) excelContainer!: ElementRef;

  isOpen = false;
  isLoading = true;
  hasError = false;
  errorMessage = '';
  fileType: FileType = 'unknown';
  safeUrl: SafeResourceUrl | null = null;
  blobUrl: string | null = null; // For PDFs
  zoomLevel = 100;
  rotation = 0;

  constructor(
    private sanitizer: DomSanitizer,
    @Optional() public dialogRef: MatDialogRef<FilePreviewComponent>,
    @Optional() @Inject(MAT_DIALOG_DATA) public dialogData: any
  ) { }

  ngOnInit(): void {
    if (this.dialogData?.file) {
      this.open(this.dialogData.file);
    } else if (this.autoOpen && this.file) {
      this.open(this.file);
    }
  }

  ngOnDestroy(): void {
    this.close();
  }

  open(file: FilePreviewData) {
    this.file = file;
    this.isOpen = true;
    this.isLoading = true;
    this.hasError = false;
    this.zoomLevel = 100;
    this.rotation = 0;

    this.detectFileType();
    this.prepareSafeUrl();
    setTimeout(() => {
        if (this.fileType === 'docx') {
        if (this.file?.base64) {
          this.renderDocx(this.file.base64);
        } else if (this.file?.url) {
          this.renderDocxFromUrl(this.file.url);
        }
      }

      if (this.fileType === 'xlsx') {
        if (this.file?.base64) {
          this.renderExcel(this.file.base64);
        } else if (this.file?.url) {
          this.renderExcelFromUrl(this.file.url);
        }
      }
    });
  }

  close() {
    this.isOpen = false;
    this.isLoading = false;
    this.hasError = false;

    if (this.blobUrl) {
      URL.revokeObjectURL(this.blobUrl);
      this.blobUrl = null;
    }

    this.closed.emit();
    if (this.dialogRef) {
      this.dialogRef.close();
    }
  }

  private detectFileType(): void {
    if (!this.file) {
      this.fileType = 'unknown';
      return;
    }

    const name = (this.file.name || '').toLowerCase();
    const type = (this.file.type || '').toLowerCase();

    if (name.match(/\.(jpg|jpeg|png|gif|webp|svg)$/) || type.startsWith('image/') || /(jpg|jpeg|png|gif|webp|svg)/.test(type)) {
      this.fileType = 'image';
    }
    else if (name.endsWith('.pdf') || type.includes('pdf')) {
      this.fileType = 'pdf';
    }
    else if (name.endsWith('.docx') || type.includes('docx')) {
      this.fileType = 'docx';
    }
    else if (
      name.endsWith('.xlsx') ||
      name.endsWith('.xls') ||
      type.includes('excel') ||
      type.includes('spreadsheetml') ||
      type.includes('application/vnd.ms-excel') ||
      type.includes('application/vnd.openxmlformats-officedocument.spreadsheetml.sheet')
    ) {
      this.fileType = 'xlsx';
    }
    else if (type.startsWith('video/')) {
      this.fileType = 'video';
    }
    else if (type.startsWith('audio/')) {
      this.fileType = 'audio';
    }
    else {
      this.fileType = 'unknown';
    }
  }

  private prepareSafeUrl(): void {
    if (!this.file) return;

    try {
      let url = this.file.url;
      if (this.fileType === 'pdf' && this.file.base64) {
        try {
          const cleanBase64 = this.file.base64.replace(/\s/g, '');
          const byteCharacters = atob(cleanBase64);
          const byteNumbers = new Array(byteCharacters.length);
          for (let i = 0; i < byteCharacters.length; i++) {
            byteNumbers[i] = byteCharacters.charCodeAt(i);
          }
          const byteArray = new Uint8Array(byteNumbers);
          const blob = new Blob([byteArray], { type: 'application/pdf' });

          this.blobUrl = URL.createObjectURL(blob);
          url = this.blobUrl;
        } catch (blobError) {
          console.error('Failed to create blob URL:', blobError);
          const cleanBase64 = this.file.base64.replace(/\s/g, '');
          url = `data:application/pdf;base64,${cleanBase64}`;
        }
      }
      else if (this.file.base64 && !url) {
        const mimeType = this.file.type || 'application/octet-stream';
        const cleanBase64 = this.cleanBase64Data(this.file.base64);
        url = `data:${mimeType};base64,${cleanBase64}`;
      }

      if (!url && this.fileType === 'docx' && this.file.base64) {
        const cleanBase64 = this.file.base64.replace(/\s/g, '');
        const binary = atob(cleanBase64);
        const len = binary.length;
        const bytes = new Uint8Array(len);
        for (let i = 0; i < len; i++) {
          bytes[i] = binary.charCodeAt(i);
        }
        const blob = new Blob([bytes], {
          type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
        });
        this.blobUrl = URL.createObjectURL(blob);
        url = this.blobUrl;
      }

      if (!url) {
        this.handleError('No URL or data provided');
        return;
      }

      this.safeUrl = this.sanitizer.bypassSecurityTrustResourceUrl(url);
      this.isLoading = false;
    } catch (error) {
      console.error('Error preparing safe URL:', error);
      this.handleError('Failed to load file');
    }
  }


  private renderDocx(base64: string) {
    const cleanBase64 = this.cleanBase64Data(base64);
    const binary = atob(cleanBase64);
    const len = binary.length;
    const bytes = new Uint8Array(len);

    for (let i = 0; i < len; i++) {
      bytes[i] = binary.charCodeAt(i);
    }

    const blob = new Blob([bytes], {
      type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
    });

    docx.renderAsync(blob, this.docxContainer.nativeElement)
      .then(() => {
        this.isLoading = false;
      })
      .catch((error: any) => {
        console.error('Error rendering DOCX from base64:', error);
        this.handleError('Failed to render document preview');
      });
  }

  private async renderDocxFromUrl(url: string) {
    try {
      const response = await fetch(url);
      const blob = await response.blob();
      await docx.renderAsync(blob, this.docxContainer.nativeElement);
      this.isLoading = false;
    } catch (error) {
      console.error('Error rendering DOCX from URL:', error);
      this.handleError('Failed to render document preview');
    }
  }

  private renderExcel(base64: string) {
    const cleanBase64 = this.cleanBase64Data(base64);
    const workbook = XLSX.read(cleanBase64, { type: 'base64' });

    const sheetName = workbook.SheetNames[0];
    const sheet = workbook.Sheets[sheetName];

    const html = XLSX.utils.sheet_to_html(sheet);

    this.excelContainer.nativeElement.innerHTML = html;

    this.isLoading = false;
  }

  private async renderExcelFromUrl(url: string) {
    try {
      if (url.startsWith('data:')) {
        const base64 = this.extractBase64FromDataUrl(url);
        this.renderExcel(base64);
        return;
      }

      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`Failed fetching spreadsheet: ${response.status}`);
      }

      const arrayBuffer = await response.arrayBuffer();
      const workbook = XLSX.read(arrayBuffer, { type: 'array' });

      const sheetName = workbook.SheetNames[0];
      const sheet = workbook.Sheets[sheetName];
      const html = XLSX.utils.sheet_to_html(sheet);

      this.excelContainer.nativeElement.innerHTML = html;
      this.isLoading = false;
    } catch (error) {
      console.error('Error rendering spreadsheet from URL:', error);
      this.handleError('Failed to render spreadsheet preview');
    }
  }

  private cleanBase64Data(base64: string): string {
    return base64.replace(/\s/g, '').replace(/^data:.*;base64,/, '');
  }

  private extractBase64FromDataUrl(dataUrl: string): string {
    const match = dataUrl.match(/^data:.*;base64,(.*)$/);
    return match ? match[1] : dataUrl;
  }

  handleError(message = 'Failed to load file'): void {
    this.hasError = true;
    this.errorMessage = message;
    this.isLoading = false;
  }

  onImageLoad(): void {
    this.isLoading = false;
  }

  onImageError(): void {
    this.handleError('Failed to load image');
  }

  onIframeLoad(): void {
    this.isLoading = false;
  }

  downloadFile(): void {
    if (!this.file) return;

    let downloadUrl = this.file.url;

    if (this.file.base64 && !downloadUrl) {
      downloadUrl = `data:${this.file.type || 'application/octet-stream'};base64,${this.file.base64}`;
    }

    const link = document.createElement('a');
    link.href = downloadUrl || '';
    link.download = this.file.name || 'download';
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    this.downloaded.emit(this.file);
  }

  openInNewTab(): void {
    if (!this.file) return;

    let openUrl = this.file.url;

    if (this.file.base64 && !openUrl) {
      openUrl = this.createBlobUrl(this.file.base64, this.file.type);
    }

    if (openUrl) {
      window.open(openUrl, '_blank', 'noopener');
    }
  }

  private createBlobUrl(base64: string, type = 'application/octet-stream'): string {
    const binary = atob(this.cleanBase64Data(base64));
    const bytes = new Uint8Array(binary.length);

    for (let index = 0; index < binary.length; index++) {
      bytes[index] = binary.charCodeAt(index);
    }

    return URL.createObjectURL(new Blob([bytes], { type }));
  }

  zoomIn(): void {
    if (this.zoomLevel < 200) {
      this.zoomLevel += 25;
    }
  }

  zoomOut(): void {
    if (this.zoomLevel > 50) {
      this.zoomLevel -= 25;
    }
  }

  resetZoom(): void {
    this.zoomLevel = 100;
    this.rotation = 0;
  }

  rotateClockwise(): void {
    this.rotation = (this.rotation + 90) % 360;
  }

  rotateCounterClockwise(): void {
    this.rotation = (this.rotation - 90 + 360) % 360;
  }

  getFileSize(): string {
    if (!this.file?.size) return '';

    const bytes = this.file.size;
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }

  getFileIcon(): string {
    const iconMap: Record<FileType, string> = {
      'image': 'image',
      'pdf': 'picture_as_pdf',
      'docx': 'insert_drive_file',
      'xlsx': 'insert_drive_file',
      'video': 'videocam',
      'audio': 'audiotrack',
      'unknown': 'insert_drive_file'
    };
    return iconMap[this.fileType] || 'insert_drive_file';
  }
}
